// Etiket tablosunun zaman aralığı — hazır seçenekler ve serbest aralık.
//
// Ayrı dosyada çünkü hem tablo hem detay alanı aynı aralığı kullanıyor ve
// dönüşüm kuralları (yerel duvar saati ↔ ISO) test edilebilir olmalı.

import { SAAT_DILIMI } from '../../lib/trFormat'

export type AralikAnahtari = '1s' | '6s' | '24s' | '7g' | '30g' | 'serbest'

export const ARALIKLAR: { anahtar: AralikAnahtari; etiket: string; saat: number | null }[] = [
  { anahtar: '1s', etiket: '1 saat', saat: 1 },
  { anahtar: '6s', etiket: '6 saat', saat: 6 },
  { anahtar: '24s', etiket: '24 saat', saat: 24 },
  { anahtar: '7g', etiket: '7 gün', saat: 24 * 7 },
  { anahtar: '30g', etiket: '30 gün', saat: 24 * 30 },
  { anahtar: 'serbest', etiket: 'Serbest', saat: null },
]

export type Aralik = { bas: string; son: string }

/**
 * Pencere sonunun yuvarlandığı adım.
 *
 * Hazır aralığın bitişi "şimdi" ama MİLİSANİYE hassasiyetinde değil: sorgu
 * anahtarları `bas`/`son` üzerinden kuruluyor ve her milisaniye yeni bir
 * anahtar demek. Kullanıcı sekmeye geri döndüğünde bileşen yeniden bağlanıp
 * `Date.now()` bir başka değer üretiyor, önbellek ıskalanıyor ve 12 binlik
 * etiket listesi baştan çekiliyordu — ekranda bu, sayfanın yenilenmesi gibi
 * görünüyor.
 *
 * Dakikaya yuvarlamak aynı dakika içindeki her dönüşü aynı anahtara
 * düşürüyor: veri önbellekten anında geliyor, tazeleme arka planda
 * `refetchInterval` ile sürüyor.
 */
export const PENCERE_KUANTUMU_MS = 60_000

/**
 * Hazır seçeneğin ISO karşılığı. Bitiş "şimdi" — akan veride sabit bir bitiş
 * kullanmak grafiği kullanıcı farkına varmadan dondururdu.
 */

export function hazirAralik(saat: number, simdi = new Date()): Aralik {
  // Aşağı yuvarlanıyor: ileri yuvarlamak henüz gelmemiş bir "şimdi" üretirdi.
  const son = new Date(Math.floor(simdi.getTime() / PENCERE_KUANTUMU_MS) * PENCERE_KUANTUMU_MS)
  const bas = new Date(son.getTime() - saat * 3600_000)
  return { bas: bas.toISOString(), son: son.toISOString() }
}

/**
 * `datetime-local` girdisinin (tesis duvar saati) ISO karşılığı.
 *
 * Girdi saat dilimi taşımıyor; tarayıcı onu YEREL saat sayar. Tesis saat
 * dilimi (§12 Europe/Istanbul) ile tarayıcınınki farklıysa doğrudan
 * `new Date(metin)` demek aralığı sessizce kaydırırdı — kullanıcı 08:00 yazar,
 * sorgu 05:00'ten başlardı. Bu yüzden fark ölçülerek düzeltiliyor.
 */
export function yerelMetindenIso(metin: string): string | null {
  if (!metin) return null

  // `new Date(metin)` bu metni TARAYICININ dilimine göre çözüyor. İstenen ise
  // TESİSİN dilimi. Fark kadar kaydırmak gerekiyor:
  //   istenen = tarayıcının_çözdüğü + (tarayıcı_farkı − tesis_farkı)
  // İki dilim aynıysa düzeltme sıfır çıkıyor; tarayıcı UTC'deyse üç saat geri.
  const yerel = new Date(metin)
  if (Number.isNaN(yerel.getTime())) return null

  const tarayiciFarki = -yerel.getTimezoneOffset() * 60_000
  return new Date(yerel.getTime() + (tarayiciFarki - kaymaMs(yerel))).toISOString()
}

/** ISO → `datetime-local` girdisinin beklediği tesis duvar saati metni. */
export function isodanYerelMetne(iso: string): string {
  const an = new Date(iso)
  if (Number.isNaN(an.getTime())) return ''
  const duvar = new Date(an.getTime() + kaymaMs(an))
  return duvar.toISOString().slice(0, 16)
}

/**
 * Verilen anda TESİS diliminin UTC'ye farkı (ms). Yaz saati geçişlerinde
 * değiştiği için sabit değil, AN'a göre hesaplanıyor.
 */
function kaymaMs(an: Date): number {
  // 'sv-SE' + timeZone → "YYYY-MM-DD HH:mm:ss" (tesis duvar saati). Aynı kalıp
  // trFormat'ta da kullanılıyor.
  const tesis = new Date(`${an.toLocaleString('sv-SE', { timeZone: SAAT_DILIMI })}Z`)
  const utc = new Date(`${an.toLocaleString('sv-SE', { timeZone: 'UTC' })}Z`)
  return tesis.getTime() - utc.getTime()
}

/** Kova genişliğini (sn) okunur yazar: grafik başlığı "5 dk'lık ortalama" der. */
export function kovaEtiketi(saniye: number): string {
  if (saniye < 60) return `${Math.round(saniye)} sn`
  if (saniye < 3600) return `${Math.round(saniye / 60)} dk`
  if (saniye < 86_400) return `${Math.round(saniye / 3600)} sa`
  return `${Math.round(saniye / 86_400)} gün`
}
